import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { AppShell } from "@/components/AppShell";
import { MatchCard, MatchData } from "@/components/MatchCard";
import { phaseLabels, phaseOrder } from "@/lib/utils-app";
import { PageMeta } from "@/components/PageMeta";
import { AdSlot } from "@/components/ads/AdSlot";

const GlobalMatches = () => {
  const { t } = useTranslation();
  const [matches, setMatches] = useState<MatchData[]>([]);
  const [phaseFilter, setPhaseFilter] = useState<string>("all");

  useEffect(() => {
    supabase.from("matches").select("*").order("kickoff_at").then(({ data }) => setMatches((data as any) ?? []));
  }, []);

  const byPhase = useMemo(() => {
    const g: Record<string, MatchData[]> = {};
    matches.forEach(m => { (g[m.phase] = g[m.phase] || []).push(m); });
    return g;
  }, [matches]);

  const availablePhases = useMemo(() => phaseOrder.filter((p) => byPhase[p]), [byPhase]);

  // Default filter: latest phase whose match is played today.
  const defaultPhase = useMemo(() => {
    const today = new Date().toDateString();
    const todaysPhases = new Set(
      matches
        .filter((m) => new Date(m.kickoff_at).toDateString() === today)
        .map((m) => m.phase),
    );
    for (let i = phaseOrder.length - 1; i >= 0; i--) {
      if (todaysPhases.has(phaseOrder[i])) return phaseOrder[i];
    }
    return "all";
  }, [matches]);

  const didInitPhase = useRef(false);
  useEffect(() => {
    if (!didInitPhase.current && availablePhases.length) {
      didInitPhase.current = true;
      if (defaultPhase !== "all" && availablePhases.includes(defaultPhase)) {
        setPhaseFilter(defaultPhase);
      }
      return;
    }
    if (phaseFilter !== "all" && !availablePhases.includes(phaseFilter)) setPhaseFilter("all");
  }, [availablePhases, phaseFilter, defaultPhase]);

  return (
    <AppShell title={t("nav.matches")}>
      <PageMeta
        title="Zápasy MS — Tipomatic"
        description="Kompletní rozpis zápasů mistrovství světa ve fotbale podle fází turnaje."
        path="/matches"
      />
      <h1 className="text-2xl font-bold mb-4">{t("nav.matches")}</h1>

      {availablePhases.length > 1 && (
        <div className="-mx-4 px-4 mb-4 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 min-w-max pb-1">
            <button
              type="button"
              onClick={() => setPhaseFilter("all")}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide border transition ${
                phaseFilter === "all"
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-background text-foreground/70 border-border hover:bg-muted"
              }`}
            >
              {t("common.all", "Vše")}
            </button>
            {availablePhases.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPhaseFilter(p)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide border transition ${
                  phaseFilter === p
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-background text-foreground/70 border-border hover:bg-muted"
                }`}
              >
                {p === "group" ? t("phases.groupShort", "Zákl. skupiny") : t(`phases.${p}`, phaseLabels[p])}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {availablePhases.filter((p) => phaseFilter === "all" || p === phaseFilter).map(p => (
          <section key={p}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t(`phases.${p}`, phaseLabels[p])}</h2>
            <div className="space-y-2">{byPhase[p].map(m => <MatchCard key={m.id} match={m} />)}</div>
          </section>
        ))}
      </div>
      <AdSlot placement="matches-feed" />
    </AppShell>
  );
};

export default GlobalMatches;
